import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import styles from './StudentSignupForm.module.css';

const StudentSignupForm = ({ onClose, onSave, onSubmit, prefilledEmail }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: prefilledEmail || '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    pickupAddress: '',
    pickupPostcode: '',
    dropoffAddress: '',
    dropoffPostcode: '',
    instructor: '',
    licenceNumber: '',
    vehicleOption: 'Automatic'
  });

  const [errors, setErrors] = useState({});
  const [sameAsPickup, setSameAsPickup] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      if (sameAsPickup) {
        if (field === 'pickupAddress') {
          updated.dropoffAddress = value;
        } else if (field === 'pickupPostcode') {
          updated.dropoffPostcode = value;
        }
      }
      
      return updated;
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSameAsPickup = (checked) => {
    setSameAsPickup(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        dropoffAddress: prev.pickupAddress,
        dropoffPostcode: prev.pickupPostcode
      }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.dropoffAddress;
        delete newErrors.dropoffPostcode;
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.pickupAddress.trim()) newErrors.pickupAddress = 'Pickup address is required';
    if (!formData.pickupPostcode.trim()) newErrors.pickupPostcode = 'Pickup postcode is required';
    if (!formData.dropoffAddress.trim()) newErrors.dropoffAddress = 'Drop off address is required';
    if (!formData.dropoffPostcode.trim()) newErrors.dropoffPostcode = 'Drop off postcode is required';
    if (!formData.licenceNumber.trim()) {
      newErrors.licenceNumber = 'Licence number is required';
    } else if (formData.licenceNumber.length !== 18) {
      newErrors.licenceNumber = 'Licence number must be exactly 18 characters';
    }
    if (!formData.vehicleOption) newErrors.vehicleOption = 'Please select a vehicle option';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const { confirmPassword, ...dataToSave } = formData;
      const submitData = {
        ...dataToSave,
        name: `${formData.firstName} ${formData.lastName}`,
        registrationDate: new Date().toISOString().split('T')[0],
        status: 'pending'
      };
      
      if (onSubmit) {
        onSubmit(submitData);
      } else if (onSave) {
        onSave(submitData);
      }
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h2>Student Signup</h2>
        {onClose && (
          <button onClick={onClose} className={styles.closeButton}>
            <X size={24} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Personal Information */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Personal Information</h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>First Name *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
              />
              {errors.firstName && <span className={styles.errorText}>{errors.firstName}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Last Name *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
              />
              {errors.lastName && <span className={styles.errorText}>{errors.lastName}</span>}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled={!!prefilledEmail}
                className={`${styles.input} ${errors.email ? styles.inputError : ''} ${prefilledEmail ? styles.inputDisabled : ''}`}
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                placeholder="+44 7700 900000"
              />
              {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Password *</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                placeholder="Minimum 8 characters"
              />
              {errors.password && <span className={styles.errorText}>{errors.password}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Confirm Password *</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
              />
              {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
            </div>
          </div>
        </div>

        {/* Date of Birth & Vehicle Preference - Side by Side */}
        <div className={styles.section}>
          <div className={styles.twoColumnRow}>
            {/* Left Column - Date of Birth */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Date of Birth *</h3>
              <div className={styles.formGroup}>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  className={`${styles.input} ${errors.dateOfBirth ? styles.inputError : ''}`}
                />
                {errors.dateOfBirth && <span className={styles.errorText}>{errors.dateOfBirth}</span>}
              </div>
            </div>

            {/* Right Column - Vehicle Preference */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Vehicle Preference</h3>
              <div className={styles.formGroup}>
                <div className={styles.vehicleButtons}>
                  <button
                    type="button"
                    className={`${styles.vehicleButton} ${formData.vehicleOption === 'Automatic' ? styles.vehicleButtonActive : ''}`}
                    onClick={() => handleChange('vehicleOption', 'Automatic')}
                  >
                    Automatic
                  </button>
                  
                  <button
                    type="button"
                    className={`${styles.vehicleButton} ${formData.vehicleOption === 'Manual' ? styles.vehicleButtonActive : ''}`}
                    onClick={() => handleChange('vehicleOption', 'Manual')}
                  >
                    Manual
                  </button>
                </div>
                {errors.vehicleOption && <span className={styles.errorText}>{errors.vehicleOption}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Pickup & Drop Off Information - Side by Side */}
        <div className={styles.section}>
          <div className={styles.twoColumnRow}>
            {/* Left Column - Pickup Information */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Pickup Information</h3>
              
              <div className={styles.formGroup}>
                <div>&nbsp;</div>
                <div>&nbsp;</div>
                <label className={styles.label}>Pickup Address *</label>
                <input
                  type="text"
                  value={formData.pickupAddress}
                  onChange={(e) => handleChange('pickupAddress', e.target.value)}
                  className={`${styles.input} ${errors.pickupAddress ? styles.inputError : ''}`}
                />
                {errors.pickupAddress && <span className={styles.errorText}>{errors.pickupAddress}</span>}
              </div>

              <div className={styles.formGroup}>
                <div>&nbsp;</div>
                <label className={styles.label}>Pickup Postcode *</label>
                <input
                  type="text"
                  value={formData.pickupPostcode}
                  onChange={(e) => handleChange('pickupPostcode', e.target.value)}
                  className={`${styles.input} ${errors.pickupPostcode ? styles.inputError : ''}`}
                  placeholder="BD1 1AA"
                />
                {errors.pickupPostcode && <span className={styles.errorText}>{errors.pickupPostcode}</span>}
              </div>
            </div>

            {/* Right Column - Drop Off Information */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Drop Off Information</h3>
              
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={sameAsPickup}
                  onChange={(e) => handleSameAsPickup(e.target.checked)}
                  className={styles.checkbox}
                />
                <span>Same as Pickup</span>
              </label>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Drop Off Address *</label>
                <input
                  type="text"
                  value={formData.dropoffAddress}
                  onChange={(e) => handleChange('dropoffAddress', e.target.value)}
                  className={`${styles.input} ${errors.dropoffAddress ? styles.inputError : ''} ${sameAsPickup ? styles.inputDisabled : ''}`}
                  disabled={sameAsPickup}
                />
                {errors.dropoffAddress && <span className={styles.errorText}>{errors.dropoffAddress}</span>}
              </div>

              <div className={styles.formGroup}>
                <div>&nbsp;</div>
                <label className={styles.label}>Drop Off Postcode *</label>
                <input
                  type="text"
                  value={formData.dropoffPostcode}
                  onChange={(e) => handleChange('dropoffPostcode', e.target.value)}
                  className={`${styles.input} ${errors.dropoffPostcode ? styles.inputError : ''} ${sameAsPickup ? styles.inputDisabled : ''}`}
                  placeholder="BD1 1AA"
                  disabled={sameAsPickup}
                />
                {errors.dropoffPostcode && <span className={styles.errorText}>{errors.dropoffPostcode}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Service Information */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Service Information</h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Instructor</label>
              <input
                type="text"
                value={formData.instructor}
                onChange={(e) => handleChange('instructor', e.target.value)}
                className={`${styles.input} ${errors.instructor ? styles.inputError : ''}`}
              />
              {errors.instructor && <span className={styles.errorText}>{errors.instructor}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Licence Number *</label>
              <input
                type="text"
                value={formData.licenceNumber}
                onChange={(e) => handleChange('licenceNumber', e.target.value)}
                className={`${styles.input} ${errors.licenceNumber ? styles.inputError : ''}`}
                placeholder="18 character code"
                maxLength={18}
              />
              {errors.licenceNumber && <span className={styles.errorText}>{errors.licenceNumber}</span>}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          {onClose && (
            <button type="button" onClick={onClose} className={styles.cancelButton}>
              Cancel
            </button>
          )}
          <button type="submit" className={styles.submitButton}>
            <Save size={18} />
            Submit Registration
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentSignupForm;